import { Fragment, useState, useCallback } from 'react'
import {
  Box,
  Table,
  Text,
  Badge,
  Flex,
  IconButton,
  Portal,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Separator,
  HStack,
} from '@chakra-ui/react'
import {
  Eye,
  Edit,
  Check,
  Trash2,
  MoreVertical,
  Copy,
  RotateCcw,
} from 'lucide-react'
import { useTransactions, useValidateTransaction, useDeleteTransaction } from '../hooks'
import { TransactionDetailDrawer } from './TransactionDetailDrawer'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import type { TransactionStatus, TransactionFilters, Transaction, TransactionLine } from '../types'

export type ViewMode = 'synthetic' | 'detailed'

interface JournalViewProps {
  filters: TransactionFilters
  viewMode: ViewMode
  onEdit?: (transaction: Transaction) => void
  onDuplicate?: (transaction: Transaction) => void
}

export function JournalView({ filters, viewMode, onEdit, onDuplicate }: JournalViewProps) {
  const { data: transactions, isLoading } = useTransactions(filters)
  const validateTransaction = useValidateTransaction()
  const deleteTransaction = useDeleteTransaction()

  // État pour le drawer
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // État pour les modales de confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [validateConfirmId, setValidateConfirmId] = useState<string | null>(null)

  const openDrawer = useCallback((id: string) => {
    setSelectedTransactionId(id)
    setIsDrawerOpen(true)
  }, [])

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false)
  }, [])

  // Navigation dans le drawer
  const currentIndex = transactions?.findIndex((t) => t.id === selectedTransactionId) ?? -1
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < (transactions?.length ?? 0) - 1

  const navigatePrevious = useCallback(() => {
    if (hasPrevious && transactions && currentIndex > 0) {
      const prevTransaction = transactions[currentIndex - 1]
      if (prevTransaction) setSelectedTransactionId(prevTransaction.id)
    }
  }, [hasPrevious, transactions, currentIndex])

  const navigateNext = useCallback(() => {
    if (hasNext && transactions && currentIndex < transactions.length - 1) {
      const nextTransaction = transactions[currentIndex + 1]
      if (nextTransaction) setSelectedTransactionId(nextTransaction.id)
    }
  }, [hasNext, transactions, currentIndex])

  const getStatusBadge = (status: TransactionStatus) => {
    const config = {
      draft: { color: 'gray', label: 'Brouillon' },
      validated: { color: 'green', label: 'Validé' },
      reversed: { color: 'red', label: 'Contrepassé' },
    }
    const { color, label } = config[status]
    return (
      <Badge colorPalette={color} variant="subtle" size="sm">
        {label}
      </Badge>
    )
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR')
  }

  const calculateTotal = (lines: TransactionLine[]) => {
    return lines?.reduce((sum, line) => sum + (line.debit || 0), 0) || 0
  }

  const handleValidate = (id: string) => {
    setValidateConfirmId(id)
  }

  const confirmValidate = async () => {
    if (validateConfirmId) {
      await validateTransaction.mutateAsync(validateConfirmId)
      setValidateConfirmId(null)
    }
  }

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id)
  }

  const confirmDelete = async () => {
    if (deleteConfirmId) {
      await deleteTransaction.mutateAsync(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }

  // Handlers pour les actions
  const handleDuplicate = useCallback((transaction: Transaction) => {
    onDuplicate?.(transaction)
  }, [onDuplicate])

  const handleReverse = (id: string) => {
    console.log('Extourner:', id)
    alert('Fonctionnalité à venir: Extourner la pièce')
  }

  const handleEdit = useCallback((transaction: Transaction) => {
    onEdit?.(transaction)
  }, [onEdit])

  // Construire le code compte pour l'affichage
  const getAccountCode = (line: TransactionLine) => {
    // Utiliser custom_code de residence_chart_account s'il existe
    const customCode = (line as any).residence_chart_account?.custom_code
    if (customCode) return customCode

    // Sinon, utiliser le code du compte de base
    return line.account?.code || '???'
  }

  if (isLoading) {
    return <Text>Chargement...</Text>
  }

  if (!transactions || transactions.length === 0) {
    return (
      <Box textAlign="center" py={8} color="gray.500">
        <Text>Aucune écriture comptable</Text>
      </Box>
    )
  }

  // Calculer les totaux
  const totals = transactions.reduce(
    (acc, tx) => {
      tx.lines?.forEach((line) => {
        acc.debit += line.debit || 0
        acc.credit += line.credit || 0
      })
      return acc
    },
    { debit: 0, credit: 0 }
  )

  return (
    <Box>
      <Box overflowX="auto">
        {viewMode === 'synthetic' ? (
          <SyntheticView
            transactions={transactions}
            selectedTransactionId={selectedTransactionId}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            calculateTotal={calculateTotal}
            getStatusBadge={getStatusBadge}
            onView={openDrawer}
            onValidate={handleValidate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onReverse={handleReverse}
            onEdit={handleEdit}
            validateTransaction={validateTransaction}
            deleteTransaction={deleteTransaction}
          />
        ) : (
          <DetailedView
            transactions={transactions}
            selectedTransactionId={selectedTransactionId}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            getStatusBadge={getStatusBadge}
            getAccountCode={getAccountCode}
            onView={openDrawer}
            onValidate={handleValidate}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onReverse={handleReverse}
            onEdit={handleEdit}
            validateTransaction={validateTransaction}
            deleteTransaction={deleteTransaction}
            totals={totals}
          />
        )}
      </Box>

      {/* Drawer de détail */}
      <TransactionDetailDrawer
        transactionId={selectedTransactionId}
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        onNavigatePrevious={navigatePrevious}
        onNavigateNext={navigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onValidate={handleValidate}
        onDuplicate={handleDuplicate}
        onReverse={handleReverse}
      />

      {/* Modal de confirmation de suppression */}
      <ConfirmDialog
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={confirmDelete}
        title="Supprimer l'écriture"
        message="Êtes-vous sûr de vouloir supprimer cette écriture brouillon ? Cette action est irréversible."
        confirmLabel="Supprimer"
        colorPalette="red"
        icon={<Trash2 size={20} />}
        isLoading={deleteTransaction.isPending}
      />

      {/* Modal de confirmation de validation */}
      <ConfirmDialog
        isOpen={!!validateConfirmId}
        onClose={() => setValidateConfirmId(null)}
        onConfirm={confirmValidate}
        title="Valider l'écriture"
        message="Une fois validée, cette écriture ne pourra plus être modifiée ni supprimée. Souhaitez-vous continuer ?"
        confirmLabel="Valider"
        colorPalette="green"
        icon={<Check size={20} />}
        isLoading={validateTransaction.isPending}
      />
    </Box>
  )
}

// Menu d'actions pour une pièce
function TransactionActionsMenu({
  transaction,
  onView,
  onValidate,
  onDelete,
  onDuplicate,
  onReverse,
  onEdit,
  validateTransaction,
  deleteTransaction,
}: {
  transaction: Transaction
  onView: (id: string) => void
  onValidate: (id: string) => void
  onDelete: (id: string) => void
  onDuplicate: (transaction: Transaction) => void
  onReverse: (id: string) => void
  onEdit?: (transaction: Transaction) => void
  validateTransaction: ReturnType<typeof useValidateTransaction>
  deleteTransaction: ReturnType<typeof useDeleteTransaction>
}) {
  const isDraft = transaction.status === 'draft'
  const isValidated = transaction.status === 'validated'
  const isReversed = transaction.status === 'reversed'

  return (
    <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
      <MenuTrigger asChild>
        <IconButton variant="ghost" size="xs" aria-label="Actions">
          <MoreVertical size={14} />
        </IconButton>
      </MenuTrigger>
      <Portal>
        <MenuPositioner>
          <MenuContent minW="180px">
            {/* Consulter - toujours disponible */}
            <MenuItem value="view" onClick={() => onView(transaction.id)}>
              <HStack gap={2}>
                <Eye size={14} />
                <Text fontSize="sm">Consulter</Text>
              </HStack>
            </MenuItem>

            <Separator />

            {/* Actions pour brouillon */}
            {isDraft && (
              <>
                <MenuItem value="edit" onClick={() => onEdit?.(transaction)}>
                  <HStack gap={2}>
                    <Edit size={14} />
                    <Text fontSize="sm">Modifier</Text>
                  </HStack>
                </MenuItem>
                <MenuItem
                  value="validate"
                  onClick={() => onValidate(transaction.id)}
                  disabled={validateTransaction.isPending}
                >
                  <HStack gap={2}>
                    <Check size={14} />
                    <Text fontSize="sm">Valider</Text>
                  </HStack>
                </MenuItem>
                <Separator />
                <MenuItem value="duplicate" onClick={() => onDuplicate(transaction)}>
                  <HStack gap={2}>
                    <Copy size={14} />
                    <Text fontSize="sm">Dupliquer</Text>
                  </HStack>
                </MenuItem>
                <Separator />
                <MenuItem
                  value="delete"
                  onClick={() => onDelete(transaction.id)}
                  color="red.600"
                  _hover={{ bg: 'red.50' }}
                  disabled={deleteTransaction.isPending}
                >
                  <HStack gap={2}>
                    <Trash2 size={14} />
                    <Text fontSize="sm">Supprimer</Text>
                  </HStack>
                </MenuItem>
              </>
            )}

            {/* Actions pour validé */}
            {isValidated && (
              <>
                <MenuItem value="reverse" onClick={() => onReverse(transaction.id)}>
                  <HStack gap={2}>
                    <RotateCcw size={14} />
                    <Text fontSize="sm">Extourner</Text>
                  </HStack>
                </MenuItem>
                <MenuItem value="duplicate" onClick={() => onDuplicate(transaction)}>
                  <HStack gap={2}>
                    <Copy size={14} />
                    <Text fontSize="sm">Dupliquer</Text>
                  </HStack>
                </MenuItem>
              </>
            )}

            {/* Actions pour contrepassé */}
            {isReversed && (
              <MenuItem value="duplicate" onClick={() => onDuplicate(transaction)}>
                <HStack gap={2}>
                  <Copy size={14} />
                  <Text fontSize="sm">Dupliquer</Text>
                </HStack>
              </MenuItem>
            )}
          </MenuContent>
        </MenuPositioner>
      </Portal>
    </MenuRoot>
  )
}

// Vue synthétique : une ligne par pièce
function SyntheticView({
  transactions,
  selectedTransactionId,
  formatDate,
  formatCurrency,
  calculateTotal,
  getStatusBadge,
  onView,
  onValidate,
  onDelete,
  onDuplicate,
  onReverse,
  onEdit,
  validateTransaction,
  deleteTransaction,
}: {
  transactions: Transaction[]
  selectedTransactionId: string | null
  formatDate: (date: string) => string
  formatCurrency: (value: number) => string
  calculateTotal: (lines: TransactionLine[]) => number
  getStatusBadge: (status: TransactionStatus) => JSX.Element
  onView: (id: string) => void
  onValidate: (id: string) => void
  onDelete: (id: string) => void
  onDuplicate: (transaction: Transaction) => void
  onReverse: (id: string) => void
  onEdit: (transaction: Transaction) => void
  validateTransaction: ReturnType<typeof useValidateTransaction>
  deleteTransaction: ReturnType<typeof useDeleteTransaction>
}) {
  return (
    <Table.Root size="sm">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="85px">Date</Table.ColumnHeader>
          <Table.ColumnHeader w="60px">Journal</Table.ColumnHeader>
          <Table.ColumnHeader w="120px">N° Pièce</Table.ColumnHeader>
          <Table.ColumnHeader>Libellé</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right" w="110px">Montant</Table.ColumnHeader>
          <Table.ColumnHeader w="85px">Statut</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right" w="60px">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {transactions.map((tx) => (
          <Table.Row
            key={tx.id}
            data-transaction-row
            cursor="pointer"
            bg={selectedTransactionId === tx.id ? 'brand.50' : 'transparent'}
            _hover={{ bg: selectedTransactionId === tx.id ? 'brand.100' : 'gray.50' }}
            onClick={() => onView(tx.id)}
          >
            <Table.Cell>{formatDate(tx.transaction_date)}</Table.Cell>
            <Table.Cell>
              <Badge variant="outline" size="sm">{tx.journal?.code}</Badge>
            </Table.Cell>
            <Table.Cell fontFamily="mono" fontSize="sm" whiteSpace="nowrap">
              {tx.piece_number || tx.reference || '-'}
            </Table.Cell>
            <Table.Cell>
              <Text truncate>{tx.description}</Text>
            </Table.Cell>
            <Table.Cell textAlign="right" fontWeight="semibold" fontFamily="mono" fontSize="sm">
              {formatCurrency(calculateTotal(tx.lines || []))}
            </Table.Cell>
            <Table.Cell>{getStatusBadge(tx.status!)}</Table.Cell>
            <Table.Cell onClick={(e) => e.stopPropagation()}>
              <Flex justify="flex-end">
                <TransactionActionsMenu
                  transaction={tx}
                  onView={onView}
                  onValidate={onValidate}
                  onDelete={onDelete}
                  onDuplicate={onDuplicate}
                  onReverse={onReverse}
                  onEdit={onEdit}
                  validateTransaction={validateTransaction}
                  deleteTransaction={deleteTransaction}
                />
              </Flex>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

// Vue détaillée : ligne d'en-tête de pièce + lignes d'écritures
function DetailedView({
  transactions,
  selectedTransactionId,
  formatDate,
  formatCurrency,
  getStatusBadge,
  getAccountCode,
  onView,
  onValidate,
  onDelete,
  onDuplicate,
  onReverse,
  onEdit,
  validateTransaction,
  deleteTransaction,
  totals,
}: {
  transactions: Transaction[]
  selectedTransactionId: string | null
  formatDate: (date: string) => string
  formatCurrency: (value: number) => string
  getStatusBadge: (status: TransactionStatus) => JSX.Element
  getAccountCode: (line: TransactionLine) => string
  onView: (id: string) => void
  onValidate: (id: string) => void
  onDelete: (id: string) => void
  onDuplicate: (transaction: Transaction) => void
  onReverse: (id: string) => void
  onEdit: (transaction: Transaction) => void
  validateTransaction: ReturnType<typeof useValidateTransaction>
  deleteTransaction: ReturnType<typeof useDeleteTransaction>
  totals: { debit: number; credit: number }
}) {
  return (
    <Table.Root size="sm">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader w="85px">Date</Table.ColumnHeader>
          <Table.ColumnHeader w="60px">Journal</Table.ColumnHeader>
          <Table.ColumnHeader w="120px">N° Pièce</Table.ColumnHeader>
          <Table.ColumnHeader w="70px">Compte</Table.ColumnHeader>
          <Table.ColumnHeader>Libellé</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right" w="110px">Débit</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right" w="110px">Crédit</Table.ColumnHeader>
          <Table.ColumnHeader w="85px">Statut</Table.ColumnHeader>
          <Table.ColumnHeader textAlign="right" w="60px">Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {transactions.map((tx, txIndex) => {
          const lines = tx.lines || []

          return (
            <Fragment key={tx.id}>
              {/* Ligne d'en-tête de la pièce */}
              <Table.Row
                data-transaction-row
                bg={selectedTransactionId === tx.id ? 'brand.50' : 'gray.100'}
                fontWeight="semibold"
                borderTop={txIndex > 0 ? '2px solid' : undefined}
                borderColor="gray.300"
                cursor="pointer"
                _hover={{ bg: selectedTransactionId === tx.id ? 'brand.100' : 'gray.150' }}
                onClick={() => onView(tx.id)}
              >
                <Table.Cell>{formatDate(tx.transaction_date)}</Table.Cell>
                <Table.Cell>
                  <Badge variant="outline" size="sm">{tx.journal?.code}</Badge>
                </Table.Cell>
                <Table.Cell fontFamily="mono" fontSize="sm" whiteSpace="nowrap">
                  {tx.piece_number || tx.reference || '-'}
                </Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell>
                  <Text fontSize="sm" fontWeight="semibold">{tx.description}</Text>
                </Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell>{getStatusBadge(tx.status!)}</Table.Cell>
                <Table.Cell onClick={(e) => e.stopPropagation()}>
                  <Flex justify="flex-end">
                    <TransactionActionsMenu
                      transaction={tx}
                      onView={onView}
                      onValidate={onValidate}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      onReverse={onReverse}
                      onEdit={onEdit}
                      validateTransaction={validateTransaction}
                      deleteTransaction={deleteTransaction}
                    />
                  </Flex>
                </Table.Cell>
              </Table.Row>

              {/* Lignes d'écritures */}
              {lines.map((line) => (
                <Table.Row
                  key={line.id}
                  bg="white"
                  _hover={{ bg: 'gray.50' }}
                >
                  <Table.Cell></Table.Cell>
                  <Table.Cell></Table.Cell>
                  <Table.Cell></Table.Cell>
                  <Table.Cell fontFamily="mono" fontSize="sm" fontWeight="medium" color="blue.700">
                    {getAccountCode(line)}
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" color="gray.700" pl={2}>
                      {line.label || tx.description || '-'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm">
                    {line.debit && line.debit > 0 ? formatCurrency(line.debit) : ''}
                  </Table.Cell>
                  <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm">
                    {line.credit && line.credit > 0 ? formatCurrency(line.credit) : ''}
                  </Table.Cell>
                  <Table.Cell></Table.Cell>
                  <Table.Cell></Table.Cell>
                </Table.Row>
              ))}
            </Fragment>
          )
        })}

        {/* Ligne de totaux */}
        <Table.Row bg="gray.200" fontWeight="bold" borderTop="2px solid" borderColor="gray.400">
          <Table.Cell colSpan={5} textAlign="right" pr={4}>
            TOTAUX
          </Table.Cell>
          <Table.Cell textAlign="right" fontFamily="mono">
            {formatCurrency(totals.debit)}
          </Table.Cell>
          <Table.Cell textAlign="right" fontFamily="mono">
            {formatCurrency(totals.credit)}
          </Table.Cell>
          <Table.Cell colSpan={2}>
            {totals.debit === totals.credit ? (
              <Badge colorPalette="green" size="sm">Équilibré</Badge>
            ) : (
              <Badge colorPalette="red" size="sm">
                Écart: {formatCurrency(Math.abs(totals.debit - totals.credit))}
              </Badge>
            )}
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table.Root>
  )
}
